import { apiClient } from './client';
import { createCrudApi } from './crud';
import type { ApiSuccess } from '../types';

// ---- PR ----
export interface Pr { id: number; code: string; omsId: number; boqId: number; title: string; status: string; priority: string; createdAt: string; }
export interface PrItemInput { boqItemId: number; description: string; unit: string; quantity: number; remarks?: string }
export interface CreatePrInput { omsId: number; boqId: number; title: string; requiredDate?: string; priority?: string; items: PrItemInput[] }
const prBase = createCrudApi<Pr, CreatePrInput>('purchase-requisitions');
export const prApi = {
  ...prBase,
  async changeStatus(id: number, action: string, remarks?: string) {
    const { data } = await apiClient.post<ApiSuccess<Pr>>(`/purchase-requisitions/${id}/status`, { action, remarks });
    return data.data;
  },
};

// ---- RFQ ----
export interface Rfq { id: number; code: string; prId: number; omsId: number; title: string; status: string; selectedQuotationId: number | null; selectionStatus: string | null; vendorIds?: number[]; items?: { id: number; description: string; unit: string; quantity: number }[]; }
export interface Quotation { id: number; code: string; vendor_id: number; grand_total: number; status: string }
export interface ComparisonRow { quotationId: number; code: string; vendorId: number; grandTotal: number; rank: number; savingVsHighest: number; status: string }
export const rfqApi = {
  async list(params: Record<string, unknown> = {}) {
    const { data } = await apiClient.get<ApiSuccess<Rfq[]>>('/rfqs', { params });
    return { data: data.data, meta: data.meta };
  },
  async get(id: number) { const { data } = await apiClient.get<ApiSuccess<Rfq>>(`/rfqs/${id}`); return data.data; },
  async create(input: { prId: number; title: string; dueDate?: string; vendorIds: number[] }) {
    const { data } = await apiClient.post<ApiSuccess<Rfq>>('/rfqs', input); return data.data;
  },
  async submitQuotation(id: number, input: { vendorId: number; items: { rfqItemId: number; unitRate: number; taxPercent: number }[] }) {
    const { data } = await apiClient.post<ApiSuccess<Quotation>>(`/rfqs/${id}/quotations`, input); return data.data;
  },
  async comparison(id: number) {
    const { data } = await apiClient.get<ApiSuccess<{ quotations: ComparisonRow[]; recommended: number | null }>>(`/rfqs/${id}/comparison`); return data.data;
  },
  async select(id: number, quotationId: number) { const { data } = await apiClient.post<ApiSuccess<Rfq>>(`/rfqs/${id}/select`, { quotationId }); return data.data; },
  async approveSelection(id: number) { const { data } = await apiClient.post<ApiSuccess<Rfq>>(`/rfqs/${id}/select/approve`, {}); return data.data; },
};

// ---- PO ----
export interface PoItem { id: number; description: string; unit: string; quantity: number; unitRate: number; totalAmount: number; receivedQty: number; balanceQty: number }
export interface Po { id: number; code: string; omsId: number; vendorId: number; status: string; grandTotal: number; deliveryDate: string | null; items?: PoItem[]; dispatchPlans?: unknown[]; createdAt: string; }
const poBase = createCrudApi<Po, { rfqId: number; deliveryDate?: string; terms?: string }>('purchase-orders');
export const poApi = {
  ...poBase,
  async create(input: { rfqId: number; deliveryDate?: string; terms?: string }) {
    const { data } = await apiClient.post<ApiSuccess<Po>>('/purchase-orders', input); return data.data;
  },
  async changeStatus(id: number, action: string, remarks?: string) {
    const { data } = await apiClient.post<ApiSuccess<Po>>(`/purchase-orders/${id}/status`, { action, remarks }); return data.data;
  },
  async addDispatch(id: number, input: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiSuccess<Po>>(`/purchase-orders/${id}/dispatch-plans`, input); return data.data;
  },
};

// ---- GRN ----
export const grnApi = {
  async create(input: { poId: number; receivedDate?: string; remarks?: string; items: { poItemId: number; receivedQty: number; acceptedQty?: number; rejectedQty?: number }[] }) {
    const { data } = await apiClient.post<ApiSuccess<unknown>>('/grns', input); return data.data;
  },
  async byPo(poId: number) { const { data } = await apiClient.get<ApiSuccess<{ id: number; code: string }[]>>('/grns', { params: { poId, limit: 100 } }); return data.data; },
};

// ---- Dashboard ----
export interface ProcurementDashboard {
  prValue: number; poValue: number; pendingProcurement: number; costSaving: number; procurementTatDays: number;
  materialAging: Record<string, number>; vendorPerformance: { vendorId: number; poCount: number; poValue: number }[]; prStatusCounts: Record<string, number>;
}
export const procurementApi = {
  async dashboard() { const { data } = await apiClient.get<ApiSuccess<ProcurementDashboard>>('/procurement/dashboard'); return data.data; },
};
